import React, {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {PlayerBrowseItem, PlayerScope, playersV2API} from '@/features/players/api';
import {PlayerBrowseState} from '@/features/players/types';

interface Props {
  workspaceId: string;
  playerId: string;
  selected: PeerIdentity[];
  onChange: (players: PeerIdentity[]) => void;
}

export type PeerIdentity = Pick<PlayerBrowseItem, 'id' | 'name' | 'position'>;

const browseState = (q: string, page: number): PlayerBrowseState => ({
  q,
  page,
  pageSize: 20,
  sort: 'name',
  order: 'asc',
  playedLastMatch: false,
  watching: false,
});

const PeerSearch: React.FC<{
  label: string;
  scope: PlayerScope;
  workspaceId: string;
  playerId: string;
  selected: PeerIdentity[];
  onSelect: (player: PlayerBrowseItem) => void;
}> = ({label, scope, workspaceId, playerId, selected, onSelect}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const scopeLabel = scope === 'squad' ? 'Squad' : 'Scouting';
  const query = useQuery({
    queryKey: ['ai-profile-peer-search', workspaceId, scope, search, page],
    queryFn: () => playersV2API.browse(scope, browseState(search, page), workspaceId),
    retry: false,
  });
  const selectedIds = new Set(selected.map(player => player.id));
  const results: PlayerBrowseItem[] = query.data?.data.filter((player: PlayerBrowseItem) => player.id !== playerId && !selectedIds.has(player.id)) ?? [];

  return <section className="min-w-0 space-y-2">
    <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
      {label}
      <input
        type="search"
        value={search}
        onChange={event => { setSearch(event.target.value); setPage(1); }}
        className="mt-1 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
      />
    </label>
    {query.isPending && <p role="status" className="text-sm text-gray-500 dark:text-gray-400">Searching players...</p>}
    {query.isError && <p role="alert" className="text-sm text-red-700 dark:text-red-300">Unable to search players.</p>}
    {results.length > 0 && <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
      {results.map(player => <li key={player.id}>
        <button
          type="button"
          aria-label={`Add ${player.name}`}
          onClick={() => onSelect(player)}
          className="flex min-h-11 w-full min-w-0 items-center justify-between gap-3 rounded px-2 text-left hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:hover:bg-gray-800"
        >
          <span className="min-w-0">
            <span className="block break-words font-medium text-gray-900 dark:text-gray-100">{player.name}</span>
            <span className="block break-words text-xs text-gray-500 dark:text-gray-400">{player.position} | {player.team ?? 'Team unavailable'}</span>
            <span className="block text-xs font-medium text-gray-600 dark:text-gray-300">{player.wyscoutAvailable ? 'Wyscout available' : 'Wyscout unavailable'}{player.limitedData ? ' | Limited data' : ''}</span>
          </span>
        </button>
      </li>)}
    </ul>}
    {query.data && <nav aria-label={`${scopeLabel} player pages`} className="flex items-center justify-between gap-2 text-sm">
      <button type="button" aria-label={`Previous ${scope} players`} disabled={page <= 1} onClick={() => setPage(value => value - 1)} className="min-h-11 rounded-md border border-gray-300 px-3 font-semibold disabled:opacity-50 dark:border-gray-700">Previous</button>
      <span>{scopeLabel} page {query.data.meta.page} of {Math.max(1, query.data.meta.totalPages)}</span>
      <button type="button" aria-label={`Next ${scope} players`} disabled={page >= query.data.meta.totalPages} onClick={() => setPage(value => value + 1)} className="min-h-11 rounded-md border border-gray-300 px-3 font-semibold disabled:opacity-50 dark:border-gray-700">Next</button>
    </nav>}
  </section>;
};

const AIProfilePeerSelect: React.FC<Props> = ({workspaceId, playerId, selected, onChange}) => {
  const [announcement, setAnnouncement] = useState('');
  const select = (player: PlayerBrowseItem) => {
    if (selected.some(item => item.id === player.id)) return;
    if (selected.length === 3) {
      setAnnouncement('You can select up to three peers.');
      return;
    }
    setAnnouncement('');
    onChange([...selected, {id: player.id, name: player.name, position: player.position}]);
  };

  return <div className="space-y-3 rounded-lg bg-gray-50 p-4 ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-800">
    <div>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">Comparison peers</h3>
      <p className="text-sm text-gray-600 dark:text-gray-300">Choose up to three squad or scouting players.</p>
    </div>
    {selected.length > 0 && <div aria-label="Selected peers" className="flex flex-wrap gap-2">
      {selected.map(player => <button
        key={player.id}
        type="button"
        aria-label={`Remove ${player.name}`}
        onClick={() => {
          setAnnouncement('');
          onChange(selected.filter(item => item.id !== player.id));
        }}
        className="min-h-11 rounded-full bg-club-blue-100 px-3 text-sm font-semibold text-club-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:bg-club-blue-950 dark:text-club-blue-100"
      >{player.name} <span aria-hidden="true">x</span></button>)}
    </div>}
    <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
      <PeerSearch label="Search squad players" scope="squad" workspaceId={workspaceId} playerId={playerId} selected={selected} onSelect={select} />
      <PeerSearch label="Search scouting players" scope="external" workspaceId={workspaceId} playerId={playerId} selected={selected} onSelect={select} />
    </div>
    <p role="status" aria-label="Peer selection status" aria-live="polite" className="text-sm font-medium text-amber-800 dark:text-amber-200">{announcement}</p>
  </div>;
};

export default AIProfilePeerSelect;
